import React, { useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Button, Card, PageHeader } from 'ui';
import { ApiRequest, API_AUTH_TYPES, API_ROUTES } from '../api';
import { getStoredHouseholdId } from '../helpers/authStorage';
import { handleBoundaryError } from '../utils/globalErrorHandler';

const isChunkLoadError = (error) => {
    const name = error?.name || '';
    const msg = (error?.message || '').toLowerCase();
    return name === 'ChunkLoadError'
        || (msg.includes('loading chunk') && msg.includes('failed'))
        || msg.includes("unexpected token '<'");
};

export default function ErrorBoundaryBun({ children }) {
    const [errorInfo, setErrorInfo] = useState(null);
    const errorLoggingApi = useMemo(() => new ApiRequest(API_ROUTES.ERROR_LOGGING, API_AUTH_TYPES.USER), []);

    const onError = async (error, info) => {
        if (isChunkLoadError(error)) {
            handleBoundaryError(error, info);
            return;
        }
        setErrorInfo(info);
        try {
            if (!localStorage.getItem('access_token')) {
                console.error('[ERROR-BOUNDARY] No access token; skipping server log:', error);
                return;
            }

            await errorLoggingApi.post({
                endpoint: 'log_error',
                body: {
                    household_id: getStoredHouseholdId(),
                    error_type: 'component_error',
                    error_message: error.toString(),
                    error_stack: error.stack || info.componentStack,
                    url: window.location.href,
                    component_name: info.componentStack?.split('\n')[1]?.trim() || 'Unknown',
                    user_action: 'Component rendered',
                    browser_info: {
                        userAgent: navigator.userAgent,
                        language: navigator.language,
                        platform: navigator.platform,
                        viewport: { width: window.innerWidth, height: window.innerHeight },
                    },
                    metadata: { componentStack: info.componentStack },
                },
            });
        } catch (loggingError) {
            console.error('[ERROR-BOUNDARY] Failed to log error:', loggingError);
        }
    };

    return <ErrorBoundary onError={onError} fallbackRender={({ error }) => (
        <main className="bun flex min-h-screen items-center justify-center px-4 py-8">
            <Card className="w-full max-w-md">
                <Card.Section className="space-y-5">
                    <PageHeader title="Something went wrong" />
                    <p className="m-0 text-bun-body font-book leading-relaxed text-bun-stone">
                        We're sorry, but something unexpected happened. Our team has been notified and is working to fix the issue.
                    </p>
                    <Button className="w-full" onClick={() => window.location.reload()}>Reload Page</Button>
                    {process.env.NODE_ENV === 'development' && errorInfo && (
                        <Card.Disclosure title="Error Details (Development Only)">
                            <p className="font-strong text-bun-body">{error.toString()}</p>
                            <pre className="whitespace-pre-wrap break-words text-bun-meta">{errorInfo.componentStack}</pre>
                        </Card.Disclosure>
                    )}
                </Card.Section>
            </Card>
        </main>
    )}>{children}</ErrorBoundary>;
}
